import type { SiteResource } from '@/types/site';

type SiteResourceRecord = Record<string, unknown>;

type SiteResourceSource = Partial<
  Pick<
    SiteResource,
    | 'contentJson'
    | 'contentSchema'
    | 'coverUrl'
    | 'fileSize'
    | 'id'
    | 'contentJson'
    | 'contentSchema'
    | 'mimeType'
    | 'primaryText'
    | 'primaryUrl'
    | 'metaJson'
    | 'resourceCode'
    | 'resourceSubtype'
    | 'resourceType'
    | 'sourceType'
    | 'storageType'
    | 'schemaVersion'
  >
> & {
  content?: unknown;
};

type ParsedBaseResource = {
  content: SiteResourceRecord;
  schemaName: string;
  schemaVersion: number;
};

export type ParsedSiteResource =
  | (ParsedBaseResource & {
      type: 'contact';
      address?: string;
      email?: string;
      phone?: string;
    })
  | (ParsedBaseResource & {
      type: 'phone';
      label?: string;
      phone?: string;
    })
  | (ParsedBaseResource & {
      type: 'email';
      email?: string;
      label?: string;
    })
  | (ParsedBaseResource & {
      type: 'address';
      address?: string;
      label?: string;
    })
  | (ParsedBaseResource & {
      type: 'faq';
      answer?: string;
      question?: string;
    })
  | (ParsedBaseResource & {
      type: 'copy';
      body?: string;
      summary?: string;
      title?: string;
    })
  | (ParsedBaseResource & {
      type: 'offer';
      description?: string;
      title?: string;
      url?: string;
    })
  | (ParsedBaseResource & {
      type: 'image';
      alt?: string;
      height?: number;
      objectName?: string;
      url: string;
      width?: number;
    })
  | (ParsedBaseResource & {
      type: 'file';
      fileName?: string;
      objectName?: string;
      url: string;
    })
  | (ParsedBaseResource & {
      type: 'pdf';
      fileName?: string;
      objectName?: string;
      url: string;
    })
  | (ParsedBaseResource & {
      type: 'url';
      label?: string;
      target?: string;
      url: string;
    })
  | (ParsedBaseResource & {
      type: 'template';
      body?: string;
      subtype: string;
      title?: string;
      url?: string;
    })
  | (ParsedBaseResource & {
      type: 'unknown';
    });

type ResourceDraftLike = {
  id?: number;
  resourceCode: string;
  resourceType: 'asset' | 'business' | 'content' | 'template';
  subtype: string;
  title: string;
  locator: string;
  note: string;
  phone: string;
  email: string;
  address: string;
  question: string;
  answer: string;
  body: string;
  description: string;
  target: string;
  mimeType?: string;
  fileSize?: number;
  coverUrl?: string;
  objectName?: string;
  metaJson?: string;
  sourceType?: string;
  storageType?: string;
  contentSchema?: string;
};

const URL_RESOURCE_SUBTYPES = new Set([
  'url',
  'official-website',
  'internal-link',
  'reference-website',
]);

const normalizeStringValue = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const normalizeNumberValue = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const normalizeCodePart = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .replace(/\.{2,}/g, '.');

const buildResourceCodeSuffix = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
};

const extractResourceCodeSuffix = (existingCode?: string) => {
  const normalized = normalizeCodePart(existingCode || '');
  if (!normalized) return buildResourceCodeSuffix();

  const segments = normalized.split('.');
  if (segments.length < 3) return buildResourceCodeSuffix();

  return segments.slice(2).join('.') || buildResourceCodeSuffix();
};

const getStringFromContent = (content: SiteResourceRecord, keys: string[]) => {
  for (const key of keys) {
    const value = normalizeStringValue(content[key]);
    if (value) return value;
  }
  return undefined;
};

export const createSiteResourceCode = (kind: string, subtype: string, existingCode?: string) => {
  const kindPart = normalizeCodePart(kind) || 'asset';
  const subtypePart = normalizeCodePart(subtype) || kindPart;
  const suffix = extractResourceCodeSuffix(existingCode);

  return `${kindPart}.${subtypePart}.${suffix}`;
};

export const parseSiteResourceRecord = (value?: string | SiteResourceRecord | null) => {
  if (!value) return {};

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as SiteResourceRecord;
  }

  if (typeof value !== 'string') return {};

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as SiteResourceRecord)
      : {};
  } catch {
    return {};
  }
};

export const normalizeSiteResourceSchemaName = (value?: string) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (!normalized) return '';
  const schema = normalized.replace(/\.v\d+$/, '');
  if (schema === 'pdf' || schema === 'file') return 'asset.pdf';
  if (schema === 'link' || schema === 'url') return 'asset.url';
  if (schema === 'image') return 'asset.image';
  return schema;
};

const inferSiteResourceSchemaName = (resource: SiteResourceSource, content: SiteResourceRecord) => {
  const resourceType = String(resource.resourceType || '')
    .trim()
    .toLowerCase();
  const subtype = String(resource.resourceSubtype || '')
    .trim()
    .toLowerCase();
  const mimeType = String(resource.mimeType || '')
    .trim()
    .toLowerCase();
  const url = String(
    normalizeStringValue(content.url) || normalizeStringValue(resource.primaryUrl) || '',
  )
    .trim()
    .toLowerCase();

  if (resourceType === 'business' && subtype === 'contact') return 'business.contact';
  if (resourceType === 'business' && subtype === 'phone') return 'business.phone';
  if (resourceType === 'business' && subtype === 'email') return 'business.email';
  if (resourceType === 'business' && subtype === 'address') return 'business.address';
  if (resourceType === 'content' && subtype === 'faq') return 'content.faq';
  if (resourceType === 'content' && subtype === 'copy') return 'content.copy';
  if (resourceType === 'content' && subtype === 'offer') return 'content.offer';
  if (resourceType === 'template' && subtype) return `template.${subtype}`;
  if (subtype === 'pdf' || mimeType === 'application/pdf' || url.endsWith('.pdf')) {
    return 'asset.pdf';
  }
  if (resourceType === 'asset' && subtype === 'file') return 'asset.file';
  if (resourceType === 'asset' && URL_RESOURCE_SUBTYPES.has(subtype)) return 'asset.url';
  if (resourceType === 'asset') return 'asset.image';
  if (resourceType && subtype) return `${resourceType}.${subtype}`;
  return resourceType || subtype || 'unknown';
};

export const getSiteResourceSchemaName = (resource: SiteResourceSource) => {
  const content = parseSiteResourceRecord(
    resource.contentJson ||
      ((resource.content && typeof resource.content === 'object'
        ? (resource.content as SiteResourceRecord)
        : null) as SiteResourceRecord | null),
  );

  return (
    normalizeSiteResourceSchemaName(resource.contentSchema) ||
    inferSiteResourceSchemaName(resource, content)
  );
};

export const parseSiteResource = (resource: SiteResourceSource): ParsedSiteResource => {
  const content = parseSiteResourceRecord(
    resource.contentJson ||
      ((resource.content && typeof resource.content === 'object'
        ? (resource.content as SiteResourceRecord)
        : null) as SiteResourceRecord | null),
  );
  const schemaName = getSiteResourceSchemaName({ ...resource, content });
  const schemaVersion = resource.schemaVersion || 1;
  const schemaKey = `${schemaName}@${schemaVersion}`;
  const primaryUrl = normalizeStringValue(resource.primaryUrl) || '';

  if (schemaVersion === 1 && schemaName.startsWith('template.')) {
    return {
      type: 'template',
      schemaName,
      schemaVersion,
      content,
      subtype: schemaName.slice('template.'.length) || 'template',
      title:
        getStringFromContent(content, ['title', 'name']) ||
        normalizeStringValue(resource.primaryText),
      body: getStringFromContent(content, ['body', 'template', 'prompt', 'markup', 'content']),
      url: normalizeStringValue(content.url) || primaryUrl || undefined,
    };
  }

  switch (schemaKey) {
    case 'business.contact@1':
      return {
        type: 'contact',
        schemaName,
        schemaVersion,
        content,
        address: normalizeStringValue(content.address),
        email: normalizeStringValue(content.email),
        phone: normalizeStringValue(content.phone),
      };

    case 'business.phone@1':
      return {
        type: 'phone',
        schemaName,
        schemaVersion,
        content,
        phone:
          normalizeStringValue(content.phone) ||
          normalizeStringValue(content.value) ||
          normalizeStringValue(resource.primaryText),
        label: normalizeStringValue(content.label),
      };

    case 'business.email@1':
      return {
        type: 'email',
        schemaName,
        schemaVersion,
        content,
        email:
          normalizeStringValue(content.email) ||
          normalizeStringValue(content.value) ||
          normalizeStringValue(resource.primaryText),
        label: normalizeStringValue(content.label),
      };

    case 'business.address@1':
      return {
        type: 'address',
        schemaName,
        schemaVersion,
        content,
        address:
          normalizeStringValue(content.address) ||
          normalizeStringValue(content.value) ||
          normalizeStringValue(resource.primaryText),
        label: normalizeStringValue(content.label),
      };

    case 'content.faq@1':
      return {
        type: 'faq',
        schemaName,
        schemaVersion,
        content,
        answer: normalizeStringValue(content.answer),
        question:
          normalizeStringValue(content.question) || normalizeStringValue(resource.primaryText),
      };

    case 'content.copy@1':
      return {
        type: 'copy',
        schemaName,
        schemaVersion,
        content,
        title:
          getStringFromContent(content, ['title', 'headline']) ||
          normalizeStringValue(resource.primaryText),
        body: getStringFromContent(content, ['body', 'text', 'content']),
        summary: getStringFromContent(content, ['summary', 'note']),
      };

    case 'content.offer@1':
      return {
        type: 'offer',
        schemaName,
        schemaVersion,
        content,
        title:
          getStringFromContent(content, ['title', 'headline']) ||
          normalizeStringValue(resource.primaryText),
        description: getStringFromContent(content, ['description', 'body', 'summary']),
        url: normalizeStringValue(content.url) || primaryUrl || undefined,
      };

    case 'asset.image@1':
      return {
        type: 'image',
        schemaName,
        schemaVersion,
        content,
        alt: normalizeStringValue(content.alt),
        height: normalizeNumberValue(content.height),
        objectName: normalizeStringValue(content.objectName),
        url: normalizeStringValue(content.url) || primaryUrl,
        width: normalizeNumberValue(content.width),
      };

    case 'asset.file@1':
      return {
        type: 'file',
        schemaName,
        schemaVersion,
        content,
        fileName: normalizeStringValue(content.fileName),
        objectName: normalizeStringValue(content.objectName),
        url: normalizeStringValue(content.url) || primaryUrl,
      };

    case 'asset.pdf@1':
      return {
        type: 'pdf',
        schemaName,
        schemaVersion,
        content,
        fileName: normalizeStringValue(content.fileName),
        objectName: normalizeStringValue(content.objectName),
        url: normalizeStringValue(content.url) || primaryUrl,
      };

    case 'asset.url@1':
      return {
        type: 'url',
        schemaName,
        schemaVersion,
        content,
        label: normalizeStringValue(content.label) || normalizeStringValue(resource.primaryText),
        target: normalizeStringValue(content.target),
        url: normalizeStringValue(content.url) || primaryUrl,
      };

    default:
      return {
        type: 'unknown',
        schemaName,
        schemaVersion,
        content,
      };
  }
};

const DEFAULT_SUBTYPE_BY_TYPE: Record<ResourceDraftLike['resourceType'], string> = {
  asset: 'logo',
  business: 'contact',
  content: 'copy',
  template: 'page',
};

export const truncateSiteResourceText = (value: string | undefined, maxLength = 120) => {
  const normalized = String(value || '').trim();
  if (!normalized || normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(maxLength - 3, 0))}...`;
};

export const getSiteResourceDisplayTitle = (resource: SiteResourceSource) => {
  const parsed = parseSiteResource(resource);

  switch (parsed.type) {
    case 'faq':
      return (
        parsed.question ||
        resource.primaryText ||
        resource.resourceCode ||
        `Resource #${resource.id}`
      );
    case 'email':
      return (
        parsed.label ||
        parsed.email ||
        resource.primaryText ||
        resource.resourceCode ||
        `Resource #${resource.id}`
      );
    case 'phone':
      return (
        parsed.label ||
        parsed.phone ||
        resource.primaryText ||
        resource.resourceCode ||
        `Resource #${resource.id}`
      );
    case 'address':
      return (
        parsed.label ||
        parsed.address ||
        resource.primaryText ||
        resource.resourceCode ||
        `Resource #${resource.id}`
      );
    case 'copy':
    case 'offer':
    case 'template':
      return (
        parsed.title || resource.primaryText || resource.resourceCode || `Resource #${resource.id}`
      );
    case 'url':
      return (
        parsed.label || resource.primaryText || resource.resourceCode || `Resource #${resource.id}`
      );
    case 'pdf':
    case 'file':
      return (
        resource.primaryText ||
        parsed.fileName ||
        resource.resourceCode ||
        `Resource #${resource.id}`
      );
    default:
      return resource.primaryText || resource.resourceCode || `Resource #${resource.id}`;
  }
};

export const getSiteResourceDisplayLink = (resource: SiteResourceSource) => {
  const parsed = parseSiteResource(resource);

  switch (parsed.type) {
    case 'image':
    case 'pdf':
    case 'file':
    case 'url':
    case 'offer':
    case 'template':
      return parsed.url || resource.primaryUrl || resource.resourceCode;
    case 'contact':
      return (
        [parsed.phone, parsed.email, parsed.address].filter(Boolean).join(' / ') ||
        resource.resourceCode
      );
    case 'phone':
      return parsed.phone || resource.resourceCode;
    case 'email':
      return parsed.email || resource.resourceCode;
    case 'address':
      return parsed.address || resource.resourceCode;
    case 'faq':
      return parsed.question || resource.resourceCode;
    case 'copy':
      return parsed.title || resource.resourceCode;
    default:
      return resource.primaryUrl || resource.resourceCode;
  }
};

export const getSiteResourceDisplayNote = (resource: SiteResourceSource) => {
  const parsed = parseSiteResource(resource);
  const meta = parseSiteResourceRecord(resource.metaJson);
  const metaNote = String(meta.note || '').trim();
  if (metaNote) return truncateSiteResourceText(metaNote);

  switch (parsed.type) {
    case 'contact':
      return truncateSiteResourceText(
        [parsed.phone, parsed.email, parsed.address].filter(Boolean).join(' / '),
      );
    case 'phone':
      return truncateSiteResourceText(
        parsed.phone || resource.resourceCode || 'No usage note added yet.',
      );
    case 'email':
      return truncateSiteResourceText(
        parsed.email || resource.resourceCode || 'No usage note added yet.',
      );
    case 'address':
      return truncateSiteResourceText(
        parsed.address || resource.resourceCode || 'No usage note added yet.',
      );
    case 'faq':
      return truncateSiteResourceText(
        parsed.answer || resource.resourceCode || 'No usage note added yet.',
      );
    case 'copy':
      return truncateSiteResourceText(
        parsed.summary || parsed.body || resource.resourceCode || 'No usage note added yet.',
      );
    case 'offer':
      return truncateSiteResourceText(
        parsed.description || resource.resourceCode || 'No usage note added yet.',
      );
    case 'template':
      return truncateSiteResourceText(
        parsed.body || resource.resourceCode || 'No usage note added yet.',
      );
    case 'image':
      return truncateSiteResourceText(
        parsed.alt || resource.resourceCode || 'No usage note added yet.',
      );
    case 'url':
      return truncateSiteResourceText(
        parsed.label || resource.resourceCode || 'No usage note added yet.',
      );
    case 'pdf':
    case 'file':
      return truncateSiteResourceText(
        parsed.fileName || resource.resourceCode || 'No usage note added yet.',
      );
    default:
      return truncateSiteResourceText(resource.resourceCode || 'No usage note added yet.');
  }
};

export const buildSiteResourceDraft = (resource: SiteResourceSource): ResourceDraftLike => {
  const resourceType = String(resource.resourceType || '')
    .trim()
    .toLowerCase() as ResourceDraftLike['resourceType'];
  const parsed = parseSiteResource(resource);
  const content = parseSiteResourceRecord(resource.contentJson);
  const meta = parseSiteResourceRecord(resource.metaJson);
  const subtype =
    String(resource.resourceSubtype || '')
      .trim()
      .toLowerCase() || DEFAULT_SUBTYPE_BY_TYPE[resourceType || 'asset'];

  const baseDraft: ResourceDraftLike = {
    id: resource.id,
    resourceCode: resource.resourceCode || createSiteResourceCode(resourceType || 'asset', subtype),
    resourceType: resourceType || 'asset',
    subtype,
    title: resource.primaryText || '',
    locator: resource.primaryUrl || '',
    note: String(meta.note || content.note || '').trim(),
    phone: '',
    email: '',
    address: '',
    question: '',
    answer: '',
    body: '',
    description: '',
    target: '',
    mimeType: resource.mimeType || '',
    fileSize: resource.fileSize || undefined,
    coverUrl: resource.coverUrl || resource.primaryUrl || '',
    objectName: String(content.objectName || '').trim() || undefined,
    metaJson: resource.metaJson || '',
    sourceType: resource.sourceType || '',
    storageType: resource.storageType || '',
    contentSchema: getSiteResourceSchemaName(resource),
  };

  switch (parsed.type) {
    case 'contact':
      return {
        ...baseDraft,
        address: parsed.address || '',
        email: parsed.email || '',
        phone: parsed.phone || '',
        title: resource.primaryText || '',
      };
    case 'phone':
      return {
        ...baseDraft,
        phone: parsed.phone || '',
        title: resource.primaryText || parsed.label || '',
      };
    case 'email':
      return {
        ...baseDraft,
        email: parsed.email || '',
        title: resource.primaryText || parsed.label || '',
      };
    case 'address':
      return {
        ...baseDraft,
        address: parsed.address || '',
        title: resource.primaryText || parsed.label || '',
      };
    case 'faq':
      return {
        ...baseDraft,
        answer: parsed.answer || '',
        question: parsed.question || '',
        title: resource.primaryText || '',
      };
    case 'copy':
      return {
        ...baseDraft,
        body: parsed.body || '',
        description: parsed.summary || '',
        title: parsed.title || resource.primaryText || '',
      };
    case 'offer':
      return {
        ...baseDraft,
        description: parsed.description || '',
        locator: parsed.url || resource.primaryUrl || '',
        title: parsed.title || resource.primaryText || '',
      };
    case 'template':
      return {
        ...baseDraft,
        body: parsed.body || '',
        locator: parsed.url || resource.primaryUrl || '',
        title: parsed.title || resource.primaryText || '',
      };
    case 'url':
      return {
        ...baseDraft,
        locator: parsed.url || resource.primaryUrl || '',
        target: parsed.target || '',
        title: parsed.label || resource.primaryText || '',
      };
    case 'pdf':
    case 'file':
      return {
        ...baseDraft,
        locator: parsed.url || resource.primaryUrl || '',
        objectName: parsed.objectName || baseDraft.objectName,
        title: resource.primaryText || parsed.fileName || '',
      };
    case 'image':
      return {
        ...baseDraft,
        locator: parsed.url || resource.primaryUrl || '',
        objectName: parsed.objectName || baseDraft.objectName,
        title: resource.primaryText || parsed.alt || '',
      };
    default:
      return baseDraft;
  }
};

export const createSiteResourceDraft = (): ResourceDraftLike => ({
  resourceCode: createSiteResourceCode('asset', DEFAULT_SUBTYPE_BY_TYPE.asset),
  resourceType: 'asset',
  subtype: DEFAULT_SUBTYPE_BY_TYPE.asset,
  title: '',
  locator: '',
  note: '',
  phone: '',
  email: '',
  address: '',
  question: '',
  answer: '',
  body: '',
  description: '',
  target: '_blank',
  sourceType: 'manual',
  storageType: 'inline',
  contentSchema: 'asset.image',
});

export const buildSiteResourceContent = (draft: ResourceDraftLike, locator: string) => {
  if (draft.resourceType === 'asset') {
    const normalizedSubtype = String(draft.subtype || '')
      .trim()
      .toLowerCase();
    const assetKind = URL_RESOURCE_SUBTYPES.has(normalizedSubtype)
      ? 'url'
      : normalizedSubtype === 'pdf'
        ? 'pdf'
        : normalizedSubtype === 'file'
          ? 'file'
          : 'image';

    if (assetKind === 'url') {
      return {
        label: draft.title.trim() || undefined,
        note: draft.note.trim() || undefined,
        target: draft.target.trim() || '_blank',
        url: locator,
      };
    }

    if (assetKind === 'pdf' || assetKind === 'file') {
      return {
        fileName: draft.title.trim() || undefined,
        note: draft.note.trim() || undefined,
        objectName: draft.objectName || undefined,
        url: locator,
      };
    }

    return {
      alt: draft.title.trim() || undefined,
      note: draft.note.trim() || undefined,
      objectName: draft.objectName || undefined,
      url: locator,
    };
  }

  if (draft.resourceType === 'business') {
    if (draft.subtype === 'phone') {
      return {
        label: draft.title.trim() || undefined,
        note: draft.note.trim() || undefined,
        phone: draft.phone.trim() || undefined,
      };
    }
    if (draft.subtype === 'email') {
      return {
        email: draft.email.trim() || undefined,
        label: draft.title.trim() || undefined,
        note: draft.note.trim() || undefined,
      };
    }
    if (draft.subtype === 'address') {
      return {
        address: draft.address.trim() || undefined,
        label: draft.title.trim() || undefined,
        note: draft.note.trim() || undefined,
      };
    }
    return {
      address: draft.address.trim() || undefined,
      email: draft.email.trim() || undefined,
      note: draft.note.trim() || undefined,
      phone: draft.phone.trim() || undefined,
    };
  }

  if (draft.resourceType === 'content') {
    if (draft.subtype === 'faq') {
      return {
        answer: draft.answer.trim() || undefined,
        note: draft.note.trim() || undefined,
        question: draft.question.trim() || undefined,
      };
    }
    if (draft.subtype === 'offer') {
      return {
        description: draft.description.trim() || undefined,
        note: draft.note.trim() || undefined,
        title: draft.title.trim() || undefined,
        url: locator || undefined,
      };
    }
    return {
      body: draft.body.trim() || undefined,
      note: draft.note.trim() || undefined,
      summary: draft.description.trim() || undefined,
      title: draft.title.trim() || undefined,
    };
  }

  return {
    body: draft.body.trim() || undefined,
    note: draft.note.trim() || undefined,
    title: draft.title.trim() || undefined,
    url: locator || undefined,
  };
};
